/**
 * @component LocationSection
 * @description 본사 주소와 클립보드 복사 버튼, 네이버 지도를 제공하는 위치 안내 컴포넌트입니다.
 * @usage CompanyAboutPage의 '오시는 길' 섹션에서 사용됩니다.
 */

import { useEffect, useRef, useState } from 'react';
import { MapPin, Copy, Check } from 'lucide-react';
import { officeLocations } from '../../constants/companyData';
import NaverMap from './NaverMap';

// [비즈니스] 방문 상담은 본사에서만 진행하므로 첫 번째(본사) 정보만 사용합니다.
const office = officeLocations[0];
const fullAddress = `${office.address} ${office.addressDetail}`;

// [기능] 문자열을 클립보드에 복사합니다. 성공 여부를 boolean으로 반환합니다.
// [기술] navigator.clipboard는 HTTPS 등 보안 컨텍스트에서만 동작합니다.
//        사내망 HTTP 접속이나 구형 브라우저에서도 복사가 되도록 execCommand 경로를 남겨둡니다.
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 아래 대체 방식으로 계속 진행합니다.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // [기술] 화면에 보이지 않으면서 선택은 가능해야 하므로 뷰포트 밖으로 밀어냅니다.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export default function LocationSection() {
  // [기능] 복사 버튼 상태: 'idle' | 'copied' | 'failed'
  const [copyState, setCopyState] = useState('idle');
  const resetTimerRef = useRef(null);

  // [기술] 복사 직후 언마운트되면 타이머가 해제된 컴포넌트의 상태를 갱신하므로 정리합니다.
  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  // [기능] 본사 주소를 클립보드에 복사하고 결과를 버튼에 잠시 표시합니다.
  // [비즈니스] 브라우저 정책으로 복사가 차단될 수 있습니다. 이때 아무 반응이 없으면
  //            사용자가 버튼이 고장난 것으로 오해하므로 실패도 명시적으로 알립니다.
  const handleCopy = async () => {
    const ok = await copyToClipboard(fullAddress);

    setCopyState(ok ? 'copied' : 'failed');
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setCopyState('idle'), 2000);
  };

  const isCopied = copyState === 'copied';
  const isFailed = copyState === 'failed';

  return (
    <div className="loc-wrap">
      {/* 본사 주소 + 복사 버튼 */}
      <div className="loc-address-bar">
        <MapPin size={18} aria-hidden="true" />
        <p className="loc-address-text">
          <strong>{office.address}</strong>
          <span>{office.addressDetail}</span>
        </p>
        <button
          type="button"
          className={`loc-copy-btn${isCopied ? ' is-copied' : ''}${isFailed ? ' is-failed' : ''}`}
          onClick={handleCopy}
        >
          {isCopied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
          {isCopied ? '복사됨' : isFailed ? '복사 실패' : '복사'}
        </button>
        {/* [기능] 복사 결과를 스크린리더에 전달합니다. */}
        <span className="sr-only" role="status" aria-live="polite">
          {isCopied ? '주소가 복사되었습니다.' : ''}
          {isFailed ? '복사하지 못했습니다. 주소를 직접 선택해 복사해 주세요.' : ''}
        </span>
      </div>

      {/* [비즈니스] 복사 실패 시 대안을 안내해 사용자가 막히지 않게 합니다. */}
      {isFailed ? (
        <p className="loc-copy-hint" role="alert">
          브라우저가 복사를 차단했습니다. 위 주소를 직접 선택해 복사해 주세요.
        </p>
      ) : null}

      {/* 네이버 지도 */}
      <div className="loc-map">
        <NaverMap branches={officeLocations} selectedBranchId={office.id} />
      </div>
    </div>
  );
}
